import { Component, Mixins, Emit } from 'vue-property-decorator';
import { TheMask } from 'vue-the-mask';
import FormsMixin from '@components/shared/mixins/forms/index';
import { digits } from '@/nodes/helpers/number';
import Classes from '@components/shared/mixins/classes';
import config from '@components/shared/config';
import direction from '@components/shared/utils/direction';
import { YIcon } from '@components/index';
import Props from './props';

/**
 * @name Input
 */
@Component({
    components: {
        TheMask,
        YIcon,
    },
})
class YInput extends Mixins(Props, Classes, FormsMixin) {
    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['color', 'size', 'icon', 'sharp', 'rounded', 'flat', 'raised', 'pill', 'outline', 'bordered'];

    /**
     * The css parent class of the component
     */
    parentClass: string = this.componentClass(this.baseClass, config.prefix.input);

    /**
     * Simple types
     */
    simpleTypes: Array<string> = ['number', 'separated', 'text', 'password'];

    /**
     * Number Characters
     */
    chars : Array<string> =[
        '۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹',
        '٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩',
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
        '+', '-',
    ]

    /**
     * Format Digits based on the locale
     */
    numberTokens: {[k: string]: any} = {
        D: {
            pattern  : /[0-9\u0660-\u0669\u06F0-\u06F9]/,
            transform: (v: any) => digits(v, this.$i18n.locale),
        },
    }

    /**
     * Create Common Props for the input.
     *
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> {
        const commonProps: Record<string, any> = {
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            commonProps[item] = this[item];
        });
        return commonProps;
    }

    /**
     * Create classes array for the input.
     *
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('Input', this.commonProps, this.baseClass || config.prefix.input));
        if (this.getBoolean('disabled')) {
            cls.push('is-disabled');
        }
        if (this.getBoolean('readonly')) {
            cls.push('is-readonly');
        }

        // Get Direction Class
        if (this.type === 'password') {
            cls.push('is-ltr');
        } else if (this.dir !== 'auto') {
            cls.push(`is-${this.dir}`);
        } else {
            cls.push(direction(this.model) === 'is-rtl' ? 'is-rtl' : 'is-ltr');
        }

        return cls;
    }

    /**
     * Input Classes
     * 
     * @returns {Array} - Array of classes
     */
    get inputClasses(): Array<string> {
        const cls: Array<string> = [`${this.parentClass}__form-field`];
        if (this.isInputAlonePrepend) {
            cls.push('is-alone-prepend');
        }
        if (this.isInputAloneAppend) {
            cls.push('is-alone-append');
        }
        if (this.align) {
            cls.push(`is-text-${this.align}`);
        }

        return cls;
    }

    /**
     * Return the value
     * Return localed digits if 
     * 
     * @returns {string|number}
     */
    get valueShow(): string|number {
        if (this.getValue('type') === 'separated') {
            return this.formatSeparated(this.value);
        }
        return digits(this.value, this.$i18n.locale);
    }

    /**
     * Check if input is number
     * 
     * @returns {boolean}
     */
    get isNumberType(): boolean {
        return this.getValue('type') === 'number' || this.getValue('type') === 'separated';
    }

    /**
     * Check char is number
     *
     * @param {string} key
     * @returns {boolean}
     */
    isNumber(key: string): boolean {
        return this.chars.indexOf(key) > -1;
    }

    /**
     * Check if input is simple formatted
     * 
     * @returns {boolean}
     */
    get isSimple(): boolean {
        return this.simpleTypes.includes(this.getValue('type') || this.getValue('component_name'));
    }

    /**
     * Check if input is password input
     * 
     * @returns {boolean}
     */
    get isPassword(): boolean {
        return this.getValue('type') === 'password' || this.getValue('component_name') === 'password';
    }

    /**
     * Check if input has no prepend props
     * 
     * @returns {boolean}
     */
    get isInputAlonePrepend(): boolean {
        return !this.getBoolean('icon_prepend')
                && !this.getBoolean('addon_prepend');
    }

    /**
     * Check if input has no append props
     * 
     * @returns {boolean}
     */
    get isInputAloneAppend(): boolean {
        return !this.getBoolean('iconAppend')
                && !this.getBoolean('addonAppend')
                && !this.getBoolean('clearable');
    }

    /**
     * Check value has already decimal
     * 
     * @returns {boolean}
     */
    get hasDecimal(): boolean {
        return this.isNumberType && this.value && this.value.indexOf(this.getValue('decimal_char')) > -1;
    }

    /**
     * Decimal Separator
     * 
     * @returns {string}
     */
    get decimalCharKeyCode(): number {
        return this.getValue('decimal_char').charCodeAt(0);
    }

    /**
     * Set Direction of the form field
     * 
     * @returns {string}
     */
    get setDirection(): string {
        // TODO: Create helper for getting direction
        let dir = 'auto';

        // Use when model is nor provided
        const localeDir: string = this.$i18n.locale === 'fa' || this.$i18n.locale === 'ar' ? 'rtl' : 'ltr';
        // Use when model is provided
        const modelDir: string = direction(this.model);

        if (this.getValue('type') === 'password' && this.model) {
            dir = 'ltr';
        } else if (this.getValue('type') !== 'text' && this.model) {
            dir = 'ltr';
        } else if (this.getValue('type') !== 'text' && !this.model) {
            dir = localeDir;
        } else if (this.getValue('dir') !== 'auto') {
            dir = this.getValue('dir');
        } else {
            dir = this.model ? modelDir : localeDir;
        }
        return dir;
    }

    /**
     * Check if char is valid
     *
     * @param {object} event
     * @returns {boolean}
     */
    isValidKey(event: {[k: string]: any}): boolean {
        if (this.isNumberType) {
            const { key, keyCode } = event;
            if (keyCode === 13) {
                return true;
            }
            if (this.isNumber(key)) {
                return true;
            }
            if (this.getValue('decimal') && keyCode === this.decimalCharKeyCode && !this.hasDecimal) {
                return true;
            }
            if (this.getValue('type') === 'phone' && this.isNumber(key)) {
                return true;
            }
            return event.preventDefault();
        }
        return true;
    }

    /**
     * Format separated
     *
     * @param {string|number} value
     * @returns {string}
     */
    formatSeparated(value: string): string {
        const val: string = digits(value).replace(/[,٬\s]/g, '').replace('/', '.');
        let result: string;
        if (this.getBoolean('decimal')) {
            let arr: Array<number|string> = [];
            arr = val.split(this.getValue('decimal_char'));
            result = this.comma(arr[0]);
            result += `${this.getValue('decimal_char')}${arr[1]}`;
        } else {
            result = this.comma(val);
        }
        return digits(result, this.$i18n.locale);
    }

    /**
     * Convert to comma separated
     *
     * @param {string|number} value
     * @returns {string}
     */
    comma(value: string | number): string {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, this.getValue('thousands_separator'));
    }

    /**
     * Icon Prepend Action
     */
    iconPrependActionCaller() {
        if (this.iconPrependAction) {
            this.iconPrependAction();
        }
    }

    /**
     * Icon Append Action
     */
    iconAppendActionCaller() {
        if (this.clearable) {
            this.clear();
        } else if (this.iconAppendAction) {
            this.iconAppendAction();
        }
    }

    /**
     * Clear input value
     */
    clear(): void {
        this.model = '';
    }

    /**
     * onComplete
     *
     * @param {object} value
     * @returns {string| number}
     */
    @Emit('input')
    onInputValueChanged(value: {[k: string]: any}): string | number {
        this.model = value.target.value;
        const raw = digits(value.target.value, 'en').replace(/[,٬]/g, '').replace('/', '.');
        if (this.isNumberType) {
            return Number(raw);
        }
        return raw;
    }

    /**
     * Update masked input
     *
     * @param {string|number} event
     * @returns {string|number}
     */
    @Emit('input')
    updateMaskedInput(event: string|number): string|number {
        this.model = digits(event, 'en');
        return digits(event, 'en');
    }
}

export default YInput;
